import type { SupportedAppStatuses } from '@wix/app-market-services-types';

enum RoutePath {
  APP_DASHBOARD = 'APP_DASHBOARD',
  APP_EXTERNAL_DASHBOARD = 'APP_EXTERNAL_DASHBOARD',
  BACK = 'BACK',
  EDITOR_COMPLETE_APP_SETUP = 'EDITOR_COMPLETE_APP_SETUP',
  WIX_OFFERING = 'WIX_OFFERING',
  APP_PAGE = 'APP_PAGE',
  HOME = 'HOME',
  COLLECTION = 'COLLECTION',
  CATEGORY = 'CATEGORY',
  SUB_CATEGORY = 'SUB_CATEGORY',
  SEARCH_RESULTS = 'SEARCH_RESULTS',
  SHARE = 'SHARE',
  UNLISTED_APP = 'UNLISTED_APP',
  TEST_APP = 'TEST_APP',
  DEVELOPER = 'DEVELOPER',
  FINISH_SETUP = 'FINISH_SETUP',
  FINISH_UPDATE = 'FINISH_UPDATE',
  UNSUPPORTED = 'UNSUPPORTED',
  MANAGE_APPS = 'MANAGE_APPS',
  OPEN_APP_WITH_PARAMS = 'OPEN_APP_WITH_PARAMS',
  TOOL_PAGE = 'TOOL_PAGE',
  FOR_YOU = 'FOR_YOU',
  TOOLS = 'TOOLS',
  AI_AGENTS = 'AI_AGENTS',
}

interface BaseRoutePayload {
  referral?: string;
  hideMenu?: boolean;
  referralSectionName?: string;
  version?: string;
}

interface HomeRoutePayload extends BaseRoutePayload {}

interface ForYouRoutePayload extends BaseRoutePayload {}

interface ToolsRoutePayload extends BaseRoutePayload {}

interface CollectionRoutePayload extends BaseRoutePayload {
  slug: string;
  isSale?: boolean;
}

interface CategoryRoutePayload extends BaseRoutePayload {
  slug: string;
}

interface SubCategoryRoutePayload extends BaseRoutePayload {
  parentSlug: string;
  slug: string;
  subCat: string;
  isSale?: boolean;
}

interface SearchResultsRoutePayload extends BaseRoutePayload {
  query: string;
  isSale?: boolean;
}

interface AppDashboardRoutePayload extends BaseRoutePayload {
  appId: string;
}

interface AppExternalDashboardRoutePayload extends BaseRoutePayload {
  appId: string;
  url: string;
}

interface EditorCompleteAppSetupRoutePayload extends BaseRoutePayload {
  appId: string;
}

interface WixOfferingRoutePayload extends BaseRoutePayload {
  appId: string;
}

interface UnlistedAppPayload extends BaseRoutePayload {
  shareUrlId: string;
}

interface TestAppPayload extends BaseRoutePayload {
  shareUrlId: string;
}

interface ShareUrlRoutePayload extends BaseRoutePayload {
  appId: string;
  shareId: string;
  version?: string;
}
interface DeveloperPageRoutePayload extends BaseRoutePayload {
  slug: string;
}

interface FinishSetupRoutePayload extends BaseRoutePayload {
  appId: string;
  version: string;
  slug: string;
}
interface FinishUpdateRoutePayload extends BaseRoutePayload {
  appId: string;
  version: string;
}

interface AppPageRoutePayload extends BaseRoutePayload {
  appId?: string;
  slug?: string;
  appIndex?: string;
  status?: SupportedAppStatuses;
  searchLocation?: string;
  version?: string;
}

interface IManageAppsDeepLinkData extends BaseRoutePayload {
  appId: string;
  modal: string;
}

interface ManageAppsRoutePayload extends BaseRoutePayload {
  manageAppsDeepLinkData?: IManageAppsDeepLinkData;
}

interface OpenAppWithParamsRoutePayload extends BaseRoutePayload {
  appId: string;
  version: string;
  openAppOptions: any;
}

interface ToolPageRoutePayload extends BaseRoutePayload {
  // BM page component id of the installed tool's landing page.
  pageId: string;
  referrer: string;
}

interface AiAgentsRoutePayload extends BaseRoutePayload {}

interface RoutePayloadMap {
  [RoutePath.HOME]: HomeRoutePayload;
  [RoutePath.FOR_YOU]: ForYouRoutePayload;
  [RoutePath.TOOLS]: ToolsRoutePayload;
  [RoutePath.COLLECTION]: CollectionRoutePayload;
  [RoutePath.CATEGORY]: CategoryRoutePayload;
  [RoutePath.SUB_CATEGORY]: SubCategoryRoutePayload;
  [RoutePath.SEARCH_RESULTS]: SearchResultsRoutePayload;
  [RoutePath.APP_DASHBOARD]: AppDashboardRoutePayload;
  [RoutePath.APP_EXTERNAL_DASHBOARD]: AppExternalDashboardRoutePayload;
  [RoutePath.BACK]: undefined;
  [RoutePath.EDITOR_COMPLETE_APP_SETUP]: EditorCompleteAppSetupRoutePayload;
  [RoutePath.WIX_OFFERING]: WixOfferingRoutePayload;
  [RoutePath.APP_PAGE]: AppPageRoutePayload;
  [RoutePath.SHARE]: ShareUrlRoutePayload;
  [RoutePath.UNLISTED_APP]: UnlistedAppPayload;
  [RoutePath.TEST_APP]: TestAppPayload;
  [RoutePath.DEVELOPER]: DeveloperPageRoutePayload;
  [RoutePath.FINISH_SETUP]: FinishSetupRoutePayload;
  [RoutePath.FINISH_UPDATE]: FinishUpdateRoutePayload;
  [RoutePath.MANAGE_APPS]: ManageAppsRoutePayload | undefined;
  [RoutePath.OPEN_APP_WITH_PARAMS]: OpenAppWithParamsRoutePayload;
  [RoutePath.TOOL_PAGE]: ToolPageRoutePayload;
  [RoutePath.AI_AGENTS]: AiAgentsRoutePayload;
  [RoutePath.UNSUPPORTED]: undefined;
}

type SomeRoute<T extends RoutePath | `${RoutePath}`> = {
  path: T;
  payload: RoutePayloadMap[T extends `${infer K extends RoutePath}` ? K : T];
};

type IRoute = {
  [K in RoutePath]: SomeRoute<K>;
}[RoutePath];

export { IRoute, RoutePayloadMap, SomeRoute, RoutePath };
